/**
 * Create Organization Page
 * Two-step wizard: Step 1 Organization Details → Step 2 Primary Administrator.
 * Clean code: single responsibility, composed hooks, presentational components.
 * SOLID: page orchestrates only; logic in hooks and constants.
 */

import React, { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft } from 'lucide-react';
import { OrganizationForm, PrimaryAdministratorStep } from '../../components/createOrganization';
import { useFeedbackContext } from '../../context';
import { PrimaryButton } from '../../components/common/ButtonsV2';
import MainPageLayout from '../../components/common/MainPageLayout';
import {
  useCreateOrganizationWizard,
  useInitialSetupSubmit,
  useOrganizationForCreatePage,
} from './hooks';
import { PageHeader, ProgressBar } from './components';
import {
  INDUSTRY_OPTIONS,
  PAGE_COPY,
  ERROR_MESSAGES,
  TOAST_CONFIG,
} from './constants';

function runStep1Validation(formValidation, isStep1Valid) {
  if (formValidation.validateAll) {
    return formValidation.validateAll();
  }
  return isStep1Valid();
}

function runStep2Validation(adminFormValidation, isStep2Valid) {
  if (adminFormValidation.validateAll) {
    return adminFormValidation.validateAll();
  }
  return isStep2Valid();
}

export function CreateOrganizationPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { showToast } = useFeedbackContext();

  const orgId = useSelector(
    (state) => state.auth.user?.organization ?? state.auth.user?.organizationId,
  );
  const step = searchParams.get('step');
  const { organization: initialOrganizationData } = useOrganizationForCreatePage(orgId, step);

  const {
    currentStep,
    setCurrentStep,
    organizationData,
    adminData,
    formValidation,
    adminFormValidation,
    setOrganizationData,
    setAdminData,
    setFormValidation,
    setAdminFormValidation,
    isStep1Valid,
    isStep2Valid,
    handlePrevious,
    goToStep2,
    totalSteps,
  } = useCreateOrganizationWizard(initialOrganizationData ?? null);

  const { submitInitialSetup, isSubmitting } = useInitialSetupSubmit(
    showToast,
    dispatch,
    navigate,
  );

  const handleStep1Submit = useCallback(() => {
    if (!runStep1Validation(formValidation, isStep1Valid)) return;
    goToStep2();
  }, [formValidation, isStep1Valid, goToStep2]);

  const handleStep2Submit = useCallback(() => {
    if (!runStep2Validation(adminFormValidation, isStep2Valid)) return;

    if (!runStep1Validation(formValidation, isStep1Valid)) {
      showToast({
        variant: 'error',
        title: TOAST_CONFIG.TITLE_VALIDATION,
        copy: ERROR_MESSAGES.VALIDATION_STEP_1,
      });
      setCurrentStep(1);
      return;
    }

    submitInitialSetup(organizationData, adminData);
  }, [
    adminFormValidation,
    isStep2Valid,
    formValidation,
    isStep1Valid,
    showToast,
    setCurrentStep,
    organizationData,
    adminData,
    submitInitialSetup,
  ]);

  const canProceedStep1 = isStep1Valid();
  const canProceedStep2 = isStep2Valid();
  const isPrimaryDisabled =
    (currentStep === 1 ? !canProceedStep1 : !canProceedStep2) || isSubmitting;

  const primaryButtonLabel =
    isSubmitting
      ? PAGE_COPY.BUTTON_SUBMITTING
      : currentStep === 1
        ? PAGE_COPY.BUTTON_NEXT
        : PAGE_COPY.BUTTON_SUBMIT;

  return (
    <MainPageLayout customHeaderProps={{ hideBackButton: true }}>
      <div className="flex items-center justify-center min-h-[calc(100vh-200px)] py-8">
        <div className="w-full max-w-[734px]">
          <PageHeader
            title={PAGE_COPY.TITLE}
            currentStep={currentStep}
            totalSteps={totalSteps}
          />
          <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
          <div className="bg-white rounded-[10px] shadow-lg">
            <div className="p-12">
              {currentStep === 1 && (
                <div className="animate-fadeIn">
                  <OrganizationForm
                    industryOptions={INDUSTRY_OPTIONS}
                    initialData={organizationData}
                    onSubmit={(data, validation) => {
                      setOrganizationData(data);
                      if (validation) setFormValidation(validation);
                    }}
                    showSubmitButton={false}
                  />
                </div>
              )}

              {currentStep === 2 && (
                <div className="animate-fadeIn">
                  <PrimaryAdministratorStep
                    initialData={adminData}
                    onChange={(data, validation) => {
                      setAdminData(data);
                      if (validation) setAdminFormValidation(validation);
                    }}
                  />
                </div>
              )}
            </div>

            <div className="border-t border-[#B8B6B6]" />
            <div
              className={`px-12 py-6 flex items-center gap-[43px] ${
                currentStep === 2 ? 'justify-between' : 'justify-end'
              }`}
            >
              {currentStep === 2 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 font-['Roboto:Medium',sans-serif] text-[15px] text-[#61b8e7] underline decoration-[#61b8e7] underline-offset-2 hover:text-[#4da3d1] hover:decoration-[#4da3d1] disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline transition-colors"
                  aria-label="Go back to previous step"
                >
                  <ArrowLeft className="w-4 h-4 shrink-0" aria-hidden />
                  {PAGE_COPY.BUTTON_PREVIOUS}
                </button>
              )}
              <PrimaryButton
                onClick={currentStep === 1 ? handleStep1Submit : handleStep2Submit}
                disabled={isPrimaryDisabled}
                customClass="min-w-[253px]"
              >
                {primaryButtonLabel}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </MainPageLayout>
  );
}
